import { invert, multiply, ROOT_ID, type Matrix, type Scene } from '@flobi/artwork-core';

/**
 * Moving between the page's space and a parent's.
 *
 * Several tools worked out a placement on the page and stored it as a layer's own
 * transform, or took a move measured on the page and added it to one. That is right only
 * on the page or on an artboard that is merely moved; inside a turned, flipped or scaled
 * artboard or group the layer landed in the wrong place or moved the wrong way
 * (QA 2026-09-24: cloner-F3, cut-slice-measure-F9, arc-width-warp-F28, sel-N6).
 */

/** A page-space placement as the transform a layer stores under `parent`. */
export function toParent(scene: Scene, parent: string | null | undefined, world: Matrix): Matrix {
  if (!parent || parent === ROOT_ID) return world;
  const inverse = invert(scene.worldTransform(parent));
  return inverse ? multiply(inverse, world) : world;
}

/** A move measured on the page, in the axes of the layer's parent. */
export function deltaToParent(scene: Scene, id: string, dx: number, dy: number): { dx: number; dy: number } {
  const parent = scene.parentOf(id);
  if (!parent || parent === ROOT_ID) return { dx, dy };
  const inverse = invert(scene.worldTransform(parent));
  if (!inverse) return { dx, dy };
  return { dx: inverse[0] * dx + inverse[2] * dy, dy: inverse[1] * dx + inverse[3] * dy };
}
